import api from '../../lib/api';
import type {
  Indicator,
  IndicatorCategory,
  IndicatorPayload,
  MeasurementPayload,
  MeasurementSeries,
  ObjectivePayload,
  ObjectiveTask,
  ObjectiveTaskPayload,
  PaginatedObjectives,
  QualityObjective,
} from './types';

export interface ListObjectivesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  processArea?: string;
  periodStart?: string;
  periodEnd?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const fetchObjectives = async (
  params: ListObjectivesParams = {},
): Promise<PaginatedObjectives> => {
  const { data } = await api.get<PaginatedObjectives>('objectives', { params });
  return data;
};

export const fetchObjective = async (id: string): Promise<QualityObjective> => {
  const { data } = await api.get<QualityObjective>(`objectives/${id}`);
  return data;
};

export const createObjective = async (payload: ObjectivePayload): Promise<QualityObjective> => {
  const { data } = await api.post<QualityObjective>('objectives', payload);
  return data;
};

export const updateObjective = async (
  id: string,
  payload: Partial<ObjectivePayload>,
): Promise<QualityObjective> => {
  const { data } = await api.put<QualityObjective>(`objectives/${id}`, payload);
  return data;
};

export const createObjectiveTask = async (
  objectiveId: string,
  payload: ObjectiveTaskPayload,
): Promise<ObjectiveTask> => {
  const { data } = await api.post<ObjectiveTask>(`objectives/${objectiveId}/tasks`, payload);
  return data;
};

export const updateObjectiveTask = async (
  taskId: string,
  payload: Partial<ObjectiveTaskPayload>,
): Promise<ObjectiveTask> => {
  const { data } = await api.patch<ObjectiveTask>(`objectives/tasks/${taskId}`, payload);
  return data;
};

export interface ListIndicatorsParams {
  category?: IndicatorCategory;
  objectiveId?: string;
  active?: boolean;
}

export const fetchIndicators = async (params: ListIndicatorsParams = {}): Promise<Indicator[]> => {
  const { data } = await api.get<Indicator[]>('objectives/indicators', { params });
  return data;
};

export const fetchObjectiveIndicators = async (
  objectiveId: string,
  category?: IndicatorCategory,
): Promise<Indicator[]> => {
  const { data } = await api.get<Indicator[]>(`objectives/${objectiveId}/indicators`, {
    params: category ? { category } : undefined,
  });
  return data;
};

export const createIndicator = async (payload: IndicatorPayload): Promise<Indicator> => {
  const { data } = await api.post<Indicator>('objectives/indicators', payload);
  return data;
};

export const updateIndicator = async (
  indId: string,
  payload: Partial<IndicatorPayload>,
): Promise<Indicator> => {
  const { data } = await api.put<Indicator>(`objectives/indicators/${indId}`, payload);
  return data;
};

export const fetchIndicatorMeasurements = async (indId: string): Promise<MeasurementSeries> => {
  const { data } = await api.get<MeasurementSeries>(`objectives/indicators/${indId}/measurements`);
  return data;
};

export const createIndicatorMeasurement = async (
  indId: string,
  payload: MeasurementPayload,
) => {
  const { data } = await api.post(`objectives/indicators/${indId}/measurements`, payload);
  return data;
};

export const escalateIndicatorToNc = async (
  indId: string,
): Promise<{ indicatorId: string; qmsRecordId: string }> => {
  const { data } = await api.post<{ indicatorId: string; qmsRecordId: string }>(
    `objectives/indicators/${indId}/escalate-nc`,
  );
  return data;
};
